import {
  collection,
  getDocs,
  orderBy,
  query,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
} from "firebase/firestore";
import { SESSIONS_COLLECTION, firestore } from "../lib/firebase";
import type {
  SessionDeliverable,
  SessionDeliverableDraft,
  SessionDeliverablePreview,
  SessionDeliverableStatus,
  SessionDeliverableType,
  SessionEmailJob,
  SessionEmailJobStatus,
  SessionEmailJobType,
} from "../types/deliverables";
import type { ExpectedParticipant, TrainingSession } from "../types/session";
import { asRecord, toDate } from "../utils/firestoreData";
import {
  listSessionPublicDecks,
  listSessionPublicExercises,
  listSessionPublicQuestionnaires,
} from "./accessProjectionService";
import {
  listSessionAttendanceStatuses,
  listSessionAttendances,
} from "./attendanceService";
import { listExerciseAnswersForSession } from "./exerciseRuntimeService";
import { listQuestionnaireAnswersForSession } from "./questionnaireRuntimeService";
import { listExpectedParticipants } from "./sessionService";
import {
  queueSessionDeliverableEmailsTrusted,
  requestSessionDeliverablesTrusted,
} from "./trustedOperationsService";

const DELIVERABLES_COLLECTION = "deliverables";
const EMAIL_JOBS_COLLECTION = "emailJobs";

function normalizeDeliverableType(value: unknown): SessionDeliverableType {
  if (
    value === "participant_answers_pdf" ||
    value === "corrections_pdf" ||
    value === "attendance_pdf" ||
    value === "company_summary_pdf"
  ) {
    return value;
  }

  return "slides_pdf";
}

function normalizeDeliverableStatus(value: unknown): SessionDeliverableStatus {
  if (value === "generating" || value === "generated" || value === "failed") {
    return value;
  }

  return "queued";
}

function normalizeEmailJobType(value: unknown): SessionEmailJobType {
  return value === "company_summary"
    ? "company_summary"
    : "participant_deliverables";
}

function normalizeEmailJobStatus(value: unknown): SessionEmailJobStatus {
  if (value === "sending" || value === "sent" || value === "failed") {
    return value;
  }

  return "queued";
}

function normalizeStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function normalizeDeliverable(
  id: string,
  data: Record<string, unknown>,
): SessionDeliverable {
  return {
    checksum: typeof data.checksum === "string" ? data.checksum : null,
    error: typeof data.error === "string" ? data.error : null,
    generatedAt: toDate(data.generatedAt),
    generatedBy:
      typeof data.generatedBy === "string" ? data.generatedBy : null,
    id: typeof data.id === "string" ? data.id : id,
    participantSlotId:
      typeof data.participantSlotId === "string" ? data.participantSlotId : null,
    recipientEmail:
      typeof data.recipientEmail === "string" ? data.recipientEmail : null,
    recipientLabel:
      typeof data.recipientLabel === "string" ? data.recipientLabel : "",
    requestedAt: toDate(data.requestedAt),
    requestedBy: typeof data.requestedBy === "string" ? data.requestedBy : "",
    sessionId: typeof data.sessionId === "string" ? data.sessionId : "",
    status: normalizeDeliverableStatus(data.status),
    storagePath: typeof data.storagePath === "string" ? data.storagePath : null,
    title: typeof data.title === "string" ? data.title : "Livrable",
    type: normalizeDeliverableType(data.type),
  };
}

function normalizeEmailJob(
  id: string,
  data: Record<string, unknown>,
): SessionEmailJob {
  return {
    createdAt: toDate(data.createdAt),
    deliverableIds: normalizeStringList(data.deliverableIds),
    error: typeof data.error === "string" ? data.error : null,
    id: typeof data.id === "string" ? data.id : id,
    recipients: normalizeStringList(data.recipients),
    requestedBy: typeof data.requestedBy === "string" ? data.requestedBy : "",
    sentAt: toDate(data.sentAt),
    sessionId: typeof data.sessionId === "string" ? data.sessionId : "",
    status: normalizeEmailJobStatus(data.status),
    type: normalizeEmailJobType(data.type),
  };
}

const deliverableConverter: FirestoreDataConverter<SessionDeliverable> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): SessionDeliverable {
    return normalizeDeliverable(snapshot.id, asRecord(snapshot.data(options)));
  },
  toFirestore(value: SessionDeliverable): DocumentData {
    return value;
  },
};

const emailJobConverter: FirestoreDataConverter<SessionEmailJob> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot<DocumentData>,
    options: SnapshotOptions,
  ): SessionEmailJob {
    return normalizeEmailJob(snapshot.id, asRecord(snapshot.data(options)));
  },
  toFirestore(value: SessionEmailJob): DocumentData {
    return value;
  },
};

function sessionSubCollectionRef(sessionId: string, collectionName: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return collection(firestore, SESSIONS_COLLECTION, sessionId, collectionName);
}

function participantLabel(participant: ExpectedParticipant): string {
  return `${participant.firstName} ${participant.lastName}`.trim();
}

function assertEndedSession(session: TrainingSession) {
  if (session.status !== "ended") {
    throw new Error("Cette action est disponible uniquement après clôture.");
  }
}

function assertClosedSessionForSupport(session: TrainingSession) {
  if (session.status !== "ended" && session.status !== "archived") {
    throw new Error("Les livrables sont disponibles uniquement après clôture.");
  }
}

function participantAnswerDeliverable(
  participant: ExpectedParticipant,
): SessionDeliverableDraft {
  return {
    id: `participant_answers_${participant.id}`,
    participantSlotId: participant.id,
    recipientEmail: participant.email || null,
    recipientLabel: participantLabel(participant),
    title: `Réponses - ${participantLabel(participant)}`,
    type: "participant_answers_pdf",
  };
}

function sessionDeliverables({
  companyEmail,
  companyLabel,
  correctionCount,
  deckCount,
  participants,
}: {
  companyEmail: string;
  companyLabel: string;
  correctionCount: number;
  deckCount: number;
  participants: ExpectedParticipant[];
}): SessionDeliverableDraft[] {
  return [
    ...(deckCount > 0
      ? [
          {
            id: "slides",
            participantSlotId: null,
            recipientEmail: null,
            recipientLabel: "Tous les destinataires",
            title: "Slides de formation",
            type: "slides_pdf" as const,
          },
        ]
      : []),
    ...participants.map(participantAnswerDeliverable),
    ...(correctionCount > 0
      ? [
          {
            id: "corrections",
            participantSlotId: null,
            recipientEmail: null,
            recipientLabel: "Tous les destinataires",
            title: "Corrigés visibles",
            type: "corrections_pdf" as const,
          },
        ]
      : []),
    {
      id: "attendance",
      participantSlotId: null,
      recipientEmail: companyEmail || null,
      recipientLabel: companyLabel,
      title: "Feuille d'émargement",
      type: "attendance_pdf",
    },
    {
      id: "company_summary",
      participantSlotId: null,
      recipientEmail: companyEmail || null,
      recipientLabel: companyLabel,
      title: "Synthèse responsable entreprise",
      type: "company_summary_pdf",
    },
  ];
}

export async function buildSessionDeliverablePreview(
  session: TrainingSession,
): Promise<SessionDeliverablePreview> {
  assertClosedSessionForSupport(session);

  const [
    participants,
    decks,
    questionnaires,
    exercises,
    questionnaireAnswers,
    exerciseAnswers,
    attendances,
    attendanceStatuses,
  ] = await Promise.all([
    listExpectedParticipants(session.id),
    listSessionPublicDecks(session.id),
    listSessionPublicQuestionnaires(session.id),
    listSessionPublicExercises(session.id),
    listQuestionnaireAnswersForSession(session.id),
    listExerciseAnswersForSession(session.id),
    listSessionAttendances(session.id),
    listSessionAttendanceStatuses(session.id),
  ]);
  const correctionCount =
    questionnaires.filter((questionnaire) => questionnaire.correctionVisible)
      .length + exercises.filter((exercise) => exercise.correctionVisible).length;
  const reviewedParticipantIds = new Set([
    ...attendances.map((attendance) => attendance.participantSlotId),
    ...attendanceStatuses.map((status) => status.participantSlotId),
  ]);
  const participantsWithoutEmail = participants.filter(
    (participant) => !participant.email,
  );
  const attendanceMissingCount = Math.max(
    0,
    participants.length - reviewedParticipantIds.size,
  );
  const issues = [
    ...(attendanceMissingCount > 0
      ? [
          `${attendanceMissingCount} participant(s) sans émargement ni absence marquée.`,
        ]
      : []),
    ...(participantsWithoutEmail.length > 0
      ? [
          `${participantsWithoutEmail.length} participant(s) sans email : PDF préparés, email non planifié.`,
        ]
      : []),
    ...(!session.companyTrainingManagerEmail
      ? ["Email responsable entreprise manquant : envoi synthèse non planifié."]
      : []),
  ];
  const companyLabel = `${session.companyTrainingManagerFirstName} ${session.companyTrainingManagerLastName}`.trim() ||
    "Responsable entreprise";

  return {
    absenceCount: attendanceStatuses.length,
    correctionCount,
    deckCount: decks.length,
    deliverables: sessionDeliverables({
      companyEmail: session.companyTrainingManagerEmail,
      companyLabel,
      correctionCount,
      deckCount: decks.length,
      participants,
    }),
    exerciseAnswerCount: exerciseAnswers.length,
    issues,
    participantCount: participants.length,
    participantEmailCount: participants.length - participantsWithoutEmail.length,
    questionnaireAnswerCount: questionnaireAnswers.length,
    signedAttendanceCount: attendances.length,
  };
}

export async function listSessionDeliverables(
  sessionId: string,
): Promise<SessionDeliverable[]> {
  if (!firestore) {
    return [];
  }

  const deliverablesQuery = query(
    sessionSubCollectionRef(sessionId, DELIVERABLES_COLLECTION).withConverter(
      deliverableConverter,
    ),
    orderBy("requestedAt", "desc"),
  );
  const snapshot = await getDocs(deliverablesQuery);
  return snapshot.docs.map((deliverableDoc) => deliverableDoc.data());
}

export async function listSessionEmailJobs(
  sessionId: string,
): Promise<SessionEmailJob[]> {
  if (!firestore) {
    return [];
  }

  const emailJobsQuery = query(
    sessionSubCollectionRef(sessionId, EMAIL_JOBS_COLLECTION).withConverter(
      emailJobConverter,
    ),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(emailJobsQuery);
  return snapshot.docs.map((emailJobDoc) => emailJobDoc.data());
}

export async function requestSessionDeliverables({
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<SessionDeliverable[]> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  assertEndedSession(session);
  await requestSessionDeliverablesTrusted(session.id);

  return listSessionDeliverables(session.id);
}

export async function queueSessionDeliverableEmails({
  session,
}: {
  actorUid: string;
  session: TrainingSession;
}): Promise<SessionEmailJob[]> {
  assertEndedSession(session);

  const deliverables = await listSessionDeliverables(session.id);

  if (deliverables.length === 0) {
    throw new Error("Préparez les jobs PDF avant de créer les emails.");
  }

  await queueSessionDeliverableEmailsTrusted(session.id);

  return listSessionEmailJobs(session.id);
}
